// Lieferdatum für Fellverliebt (ersetzt die App Essential Estimated Delivery). Rechnet komplett im Browser, keine externe Anfrage.
// Kalendertage laufen als UTC-Mitternacht, die Uhrzeit kommt aus Europe/Berlin.
const DAY = 864e5;
const utc = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
const iso = (d) => d.toISOString().slice(0, 10);

/** Bundesweite Feiertage DE: fest + Ostern-abhängig (Gauß/Anonymous-Algorithmus). */
function holidays(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100;
  const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((8 * b + 13) / 25)) / 3) + 15) % 30;
  const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  const easter = utc(y, Math.floor(n / 31), (n % 31) + 1).getTime();
  return [
    `${y}-01-01`, `${y}-05-01`, `${y}-10-03`, `${y}-12-25`, `${y}-12-26`,
    ...[-2, 1, 39, 50].map((o) => iso(new Date(easter + o * DAY)))
  ];
}

/** "2026-12-24, 2026-12-31" oder Bereiche "2026-12-24..2026-12-27" */
function extraDays(text) {
  const out = [];
  for (const part of (text || '').split(/[,;\s]+/)) {
    const [from, to = from] = part.split('..');
    if (!/^\d{4}-\d\d-\d\d$/.test(from) || !/^\d{4}-\d\d-\d\d$/.test(to)) continue;
    for (let t = Date.parse(from); t <= Date.parse(to); t += DAY) out.push(iso(new Date(t)));
  }
  return out;
}

/** Jetzt als Wanduhr in Berlin: { day (UTC-Mitternacht), minutes } */
function berlinNow(override) {
  const m = override?.match(/^(\d{4})-(\d\d)-(\d\d)(?:T(\d\d):(\d\d))?$/);
  if (m) return { day: utc(+m[1], +m[2], +m[3]), minutes: +(m[4] || 0) * 60 + +(m[5] || 0), fixed: true };
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
      .formatToParts(new Date()).map((x) => [x.type, +x.value])
  );
  return { day: utc(p.year, p.month, p.day), minutes: p.hour * 60 + p.minute };
}

/**
 * Reine Rechnung, auch für Tests nutzbar.
 * @param {object} c - procMin, procMax, shipMin, shipMax, procDays, shipDays ("1111100", Mo..So), cutoff (Stunde), blocked (Set ISO-Tage)
 * @param {{day: Date, minutes: number}} now
 */
export function estimate(c, now) {
  const open = (d, mask) => mask[(d.getUTCDay() + 6) % 7] === '1' && !c.blocked.has(iso(d));
  const add = (d, n, mask) => {
    let t = d.getTime();
    for (let left = n, guard = 0; left > 0 && guard < 400; guard++) {
      t += DAY;
      if (open(new Date(t), mask)) left--;
    }
    return new Date(t);
  };
  const today = now.day;
  const beforeCutoff = now.minutes < c.cutoff * 60;
  const startsToday = open(today, c.procDays) && beforeCutoff;
  const start = startsToday ? today : add(today, 1, c.procDays);
  const shipFrom = add(start, c.procMin, c.procDays);
  const shipTo = add(start, c.procMax, c.procDays);
  return {
    start, shipFrom, shipTo,
    from: add(shipFrom, c.shipMin, c.shipDays),
    to: add(shipTo, c.shipMax, c.shipDays),
    minutesLeft: startsToday ? c.cutoff * 60 - now.minutes : null
  };
}

class FvDeliveryDate extends HTMLElement {
  connectedCallback() {
    const d = this.dataset;
    const params = new URLSearchParams(location.search);
    const preview = window.Shopify && (Shopify.designMode || (Shopify.theme && Shopify.theme.role !== 'main'));
    this.override = preview ? params.get('fv_edd_now') : null;
    const foreign = d.country && d.country !== 'DE' ? +d.abroad || 0 : 0;
    const year = berlinNow(this.override).day.getUTCFullYear();
    this.config = {
      procMin: +d.procMin, procMax: Math.max(+d.procMax, +d.procMin),
      shipMin: +d.shipMin + foreign, shipMax: Math.max(+d.shipMax, +d.shipMin) + foreign,
      procDays: d.procDays, shipDays: d.shipDays, cutoff: +d.cutoff,
      blocked: new Set([...(d.holidays === 'true' ? [year, year + 1].flatMap(holidays) : []), ...extraDays(d.blocked)])
    };
    this.render();
    this.section = this.closest('.shopify-section') || document;
    this.onSelect = (e) => e.promise ? e.promise.then(() => this.render(), () => {}) : this.render();
    this.section.addEventListener('shopify:product:select', this.onSelect);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.section?.removeEventListener('shopify:product:select', this.onSelect);
  }

  render() {
    clearTimeout(this.timer);
    const d = this.dataset;
    const now = berlinNow(this.override);
    const r = estimate(this.config, now);
    const lang = document.documentElement.lang || 'de';
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    const short = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    const range = (a, b, f) => (iso(a) === iso(b) ? f.format(a) : f.formatRange ? f.formatRange(a, b) : `${f.format(a)} – ${f.format(b)}`);
    const set = (name, text) => {
      const el = this.querySelector(`[data-edd="${name}"]`);
      if (!el) return;
      el.hidden = !text;
      if (text) el.innerHTML = text;
    };
    const strong = (t) => `<strong>${t}</strong>`;

    set('main', d.text
      .replace('{von}', strong(fmt.format(r.from))).replace('{bis}', strong(fmt.format(r.to)))
      .replace('{zeitraum}', strong(range(r.from, r.to, fmt))));
    set('ordered', short.format(now.day));
    set('shipped', range(r.shipFrom, r.shipTo, short));
    set('delivered', range(r.from, r.to, short));

    const left = r.minutesLeft;
    const showCountdown = left !== null && left > 0 && left <= +d.countdownHours * 60;
    set('countdown', showCountdown ? d.countdownText.replace('{countdown}', strong([left >= 60 && `${Math.floor(left / 60)} ${d.unitH}`, left % 60 && `${left % 60} ${d.unitM}`].filter(Boolean).join(' '))) : '');

    let xmas = '';
    if (d.xmas) {
      const [mm, dd] = d.xmas.split('-').map(Number);
      const deadline = utc(now.day.getUTCFullYear(), mm, dd);
      const daysPast = (now.day - deadline) / DAY;
      if (daysPast <= 0 && daysPast > -d.xmasLead) xmas = r.to <= deadline ? d.xmasBefore : d.xmasAfter;
    }
    set('xmas', xmas);
    this.classList.add('is-ready');

    if (!now.fixed && left !== null) this.timer = setTimeout(() => this.render(), (60 - new Date().getSeconds()) * 1000);
  }
}

if (!customElements.get('fv-delivery-date')) customElements.define('fv-delivery-date', FvDeliveryDate);
